import { useEffect, useRef, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { Send, Sparkles, User as UserIcon, Wrench, RotateCw, Bot } from "lucide-react"
import { api } from "@/lib/api"
import { useTenantFilter } from "@/hooks/useTenantFilter"
import { cn } from "@/lib/utils"

type Msg = {
  role: "user" | "assistant"
  text: string
  tools?: string[]
  ts?: number
}

const SUGGESTIONS = [
  "Zeig mir die letzten 5 E-Mails",
  "Welche Termine habe ich heute?",
  "Wer hat gestern angerufen?",
  "Offene Aufgaben fuer Paul Lux",
]

/**
 * Chatbot — React-Port von /admin/chat.
 * Session-ID wird clientseitig generiert (UUID), damit die History zwischen
 * Messages erhalten bleibt. Backend laedt auf get_chatbot_history basierend
 * auf dieser session_id.
 *
 * 🔴 Der Verlauf wird beim Betreten der Seite nachgeladen (#18). Die Session
 * ueberlebte den Seitenwechsel schon immer in `sessionStorage`, `messages`
 * startete aber jedes Mal leer — wer im Assistenten etwas fragte, in einer
 * anderen Ansicht nachsah und zurueckkam, stand vor „Wie kann ich helfen?".
 * Die Nachrichten lagen die ganze Zeit in `chatbot_messages`; es fehlte
 * einzig der Abruf.
 */
export default function ChatbotPage() {
  const [messages, setMessages] = useState<Msg[]>([])
  const [input, setInput] = useState("")
  const [sessionId] = useState(() => {
    // Persist in sessionStorage → ueberlebt Page-Reloads in derselben Tab-Session
    const key = "chatbot_session_id"
    const existing = sessionStorage.getItem(key)
    if (existing) return existing
    const fresh = (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID().slice(0, 12)
      : Math.random().toString(36).slice(2, 14)
    sessionStorage.setItem(key, fresh)
    return fresh
  })
  const [model, setModel] = useState<"claude-haiku-4-5" | "claude-sonnet-4-20250514">("claude-haiku-4-5")
  const [tenant, setTenant] = useTenantFilter()
  const [streaming, setStreaming] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [ladeVerlauf, setLadeVerlauf] = useState(true)
  const bottomRef = useRef<HTMLDivElement | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)

  const tenantsDd = useQuery({
    queryKey: ["tenants-dropdown"],
    queryFn: async () => (await api.GET("/api/v1/tenants", {})).data,
    staleTime: 1000 * 60 * 5,
  })

  // Verlauf der laufenden Session einmal beim Betreten holen.
  //
  // `abgebrochen` schuetzt gegen das Rennen mit einer Frage, die der Nutzer
  // sofort abschickt: die Antwort des Servers darf die frisch getippte
  // Nachricht nicht ueberschreiben. Aus demselben Grund wird nur gesetzt,
  // solange noch nichts im Fenster steht.
  useEffect(() => {
    let abgebrochen = false
    ;(async () => {
      try {
        const res = await fetch(
          `/admin/chatbot/api/history?session_id=${encodeURIComponent(sessionId)}`,
          { credentials: "include" },
        )
        if (!res.ok) return
        const d = await res.json()
        const alte: Msg[] = (d.messages || [])
          .filter((m: { role?: string; content?: string }) =>
            (m.role === "user" || m.role === "assistant") && (m.content || "").trim())
          .map((m: { role: string; content: string; tools?: string[] }) => ({
            role: m.role as Msg["role"],
            text: m.content,
            tools: m.tools || [],
          }))
        if (abgebrochen || alte.length === 0) return
        setMessages((bisher) => (bisher.length > 0 ? bisher : alte))
      } catch {
        // Ein nicht erreichbarer Verlauf ist kein Grund, den Chat zu blockieren.
      } finally {
        if (!abgebrochen) setLadeVerlauf(false)
      }
    })()
    return () => { abgebrochen = true }
  }, [sessionId])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages, streaming])

  // Auto-resize textarea
  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = "auto"
    el.style.height = Math.min(el.scrollHeight, 180) + "px"
  }, [input])

  async function send(text?: string) {
    const toSend = (text ?? input).trim()
    if (!toSend || streaming) return
    setInput("")
    setErr(null)

    const newUser: Msg = { role: "user", text: toSend, ts: Date.now() }
    const newAssistant: Msg = { role: "assistant", text: "", tools: [], ts: Date.now() }
    setMessages((m) => [...m, newUser, newAssistant])
    setStreaming(true)

    try {
      const res = await fetch("/admin/chatbot/api/message", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          message: toSend,
          session_id: sessionId,
          model,
          tenant_slug: tenant,
        }),
      })
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`)

      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let buf = ""

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buf += decoder.decode(value, { stream: true })

        const parts = buf.split("\n\n")
        buf = parts.pop() || ""

        for (const p of parts) {
          if (!p.startsWith("data: ")) continue
          const payload = p.slice(6).trim()
          if (payload === "[DONE]") {
            setStreaming(false)
            continue
          }
          try {
            const evt = JSON.parse(payload)
            if (evt.type === "delta" && evt.text) {
              setMessages((m) => {
                const copy = [...m]
                const last = copy[copy.length - 1]
                copy[copy.length - 1] = { ...last, text: last.text + evt.text }
                return copy
              })
            } else if (evt.type === "tool") {
              setMessages((m) => {
                const copy = [...m]
                const last = copy[copy.length - 1]
                copy[copy.length - 1] = { ...last, tools: [...(last.tools || []), evt.name] }
                return copy
              })
            } else if (evt.type === "error") {
              setErr(evt.text)
              setStreaming(false)
            }
          } catch { /* noop */ }
        }
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setStreaming(false)
    }
  }

  const newChat = () => {
    if (messages.length > 0 && !confirm("Chat-Verlauf loeschen und neu starten?")) return
    setMessages([])
    const fresh = crypto.randomUUID?.().slice(0, 12) || Math.random().toString(36).slice(2, 14)
    sessionStorage.setItem("chatbot_session_id", fresh)
    window.location.reload()
  }

  return (
    <div
      className="flex flex-col mx-auto relative"
      style={{
        maxWidth: 960,
        height: "calc(100vh - 40px)",
      }}
    >
      {/* Header — sticky mit glass-effect */}
      <div
        className="sticky top-0 z-10 backdrop-blur-md"
        style={{
          background: "rgba(255,255,255,0.85)",
          borderBottom: "1px solid rgba(15,23,42,0.08)",
          padding: "12px 20px",
          borderRadius: "0 0 16px 16px",
        }}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div
              style={{
                width: 38, height: 38, borderRadius: 12,
                background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                display: "flex", alignItems: "center", justifyContent: "center",
                boxShadow: "0 4px 12px rgba(99,102,241,0.3)",
              }}
            >
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-[16px] font-bold leading-tight">Assistent</h1>
              <div className="text-[11px] text-muted-foreground">
                {streaming ? "tippt…" : `Session #${sessionId.slice(0, 6)}`}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={tenant}
              onChange={(e) => setTenant(e.target.value)}
              className="h-8 rounded-lg border bg-white/70 px-2.5 text-[12px] shadow-xs"
              title="Tenant-Kontext"
            >
              <option value="">Alle Tenants</option>
              {tenantsDd.data?.items?.map((t) => (
                <option key={t.slug} value={t.slug}>{t.name}</option>
              ))}
            </select>
            <select
              value={model}
              onChange={(e) => setModel(e.target.value as typeof model)}
              className="h-8 rounded-lg border bg-white/70 px-2.5 text-[12px] shadow-xs"
            >
              {/* Die Werte sind nur Schalter „schnell“/„gruendlich“ — welches Modell
                  dahinter steht, sagt die .env (MODEL_CHATBOT / MODEL_CHATBOT_STARK,
                  Baustein 14). Die Beschriftung nennt deshalb kein Modell. */}
              <option value="claude-haiku-4-5">⚡ Schnell</option>
              <option value="claude-sonnet-4-20250514">🎯 Gründlich</option>
            </select>
            <button
              onClick={newChat}
              className="h-8 px-3 rounded-lg text-[12px] font-medium inline-flex items-center gap-1.5 hover:bg-secondary transition-colors"
              title="Neuen Chat starten"
            >
              <RotateCw className="w-3.5 h-3.5" /> Neu
            </button>
          </div>
        </div>
      </div>

      {/* Messages Area */}
      <div
        className="flex-1 overflow-y-auto"
        style={{ padding: "24px 20px", scrollBehavior: "smooth" }}
      >
        {ladeVerlauf && messages.length === 0 ? (
          // Kein Aufblitzen der Startansicht, waehrend der Verlauf unterwegs
          // ist — sonst sieht jeder Rueckkehrer erst „Wie kann ich helfen?"
          // und einen Wimpernschlag spaeter doch seinen Chat.
          <div className="text-center py-10 text-[13px] text-muted-foreground">
            Verlauf wird geladen…
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-10 max-w-[520px] mx-auto">
            <div
              style={{
                width: 72, height: 72, margin: "0 auto 20px", borderRadius: 20,
                background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                display: "flex", alignItems: "center", justifyContent: "center",
                boxShadow: "0 12px 32px rgba(99,102,241,0.35)",
              }}
            >
              <Bot className="w-9 h-9 text-white" />
            </div>
            <h2 className="text-[20px] font-bold mb-2">Wie kann ich helfen?</h2>
            <p className="text-[13.5px] text-muted-foreground mb-6 leading-relaxed">
              Ich habe Zugriff auf 21 Tools — E-Mails, Anrufe, Kalender, Kontakte, Aufgaben, HERO, Vorgänge.
              Frag einfach.
            </p>
            <div className="flex flex-wrap gap-2 justify-center">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  className="text-[12.5px] px-3.5 py-2 rounded-full border bg-white hover:bg-secondary transition-colors shadow-xs"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-5 pb-2">
            {messages.map((m, i) => (
              <MessageBubble
                key={i}
                msg={m}
                isLast={i === messages.length - 1}
                streaming={streaming}
              />
            ))}
          </div>
        )}
        {err && (
          <div
            className="my-3 p-3 rounded-lg text-[12.5px]"
            style={{ background: "#fee2e2", color: "#991b1b", border: "1px solid #fca5a5" }}
          >
            ⚠ {err}
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input Area */}
      <div
        className="sticky bottom-0"
        style={{
          padding: "12px 20px 16px",
          background: "linear-gradient(to top, rgba(255,255,255,1) 70%, rgba(255,255,255,0) 100%)",
        }}
      >
        <form
          onSubmit={(ev) => { ev.preventDefault(); send() }}
          className="relative flex items-end gap-2 rounded-2xl border bg-white p-2 shadow-md"
          style={{ borderColor: "#e2e8f0" }}
        >
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send() }
            }}
            placeholder="Nachricht an den Assistenten …"
            rows={1}
            className="flex-1 bg-transparent px-2.5 py-2 text-[14px] resize-none focus:outline-hidden"
            style={{ maxHeight: 180, lineHeight: "1.5" }}
          />
          <button
            type="submit"
            disabled={!input.trim() || streaming}
            className={cn(
              "h-10 w-10 rounded-xl inline-flex items-center justify-center transition-all self-end shrink-0",
              (!input.trim() || streaming)
                ? "bg-muted text-muted-foreground cursor-not-allowed"
                : "text-white hover:shadow-lg active:scale-95",
            )}
            style={
              (!input.trim() || streaming)
                ? {}
                : { background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)", boxShadow: "0 4px 12px rgba(99,102,241,0.35)" }
            }
            aria-label="Senden"
          >
            <Send className="w-4 h-4" />
          </button>
        </form>
        <div className="text-center mt-2 text-[10.5px] text-muted-foreground">
          Enter senden · Shift+Enter Zeilenumbruch · Kontext bleibt pro Session erhalten
        </div>
      </div>
    </div>
  )
}

function MessageBubble({ msg, isLast, streaming }: { msg: Msg; isLast: boolean; streaming: boolean }) {
  const isUser = msg.role === "user"
  const showTyping = streaming && isLast && !isUser && !msg.text

  return (
    <div className={cn("flex gap-3", isUser ? "flex-row-reverse" : "flex-row")}>
      <div
        className="shrink-0"
        style={{
          width: 32, height: 32, borderRadius: 10,
          background: isUser
            ? "linear-gradient(135deg, #0f172a 0%, #334155 100%)"
            : "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: isUser ? "0 2px 6px rgba(15,23,42,0.2)" : "0 2px 6px rgba(99,102,241,0.3)",
        }}
      >
        {isUser
          ? <UserIcon className="w-4 h-4 text-white" />
          : <Sparkles className="w-4 h-4 text-white" />}
      </div>
      <div className={cn("max-w-[82%] space-y-2", isUser ? "items-end" : "items-start")}>
        {msg.tools && msg.tools.length > 0 && (
          <div className={cn("flex flex-wrap gap-1.5", isUser ? "justify-end" : "justify-start")}>
            {msg.tools.map((t, j) => (
              <span
                key={j}
                className="inline-flex items-center gap-1 text-[10.5px] font-medium px-2 py-0.5 rounded-full"
                style={{
                  background: "rgba(99,102,241,0.08)",
                  color: "#4338ca",
                  border: "1px solid rgba(99,102,241,0.18)",
                }}
              >
                <Wrench className="w-2.5 h-2.5" /> {t}
              </span>
            ))}
          </div>
        )}
        <div
          data-pii
          className={cn(
            "rounded-2xl px-4 py-2.5 text-[13.5px] leading-relaxed whitespace-pre-wrap",
            isUser ? "rounded-tr-sm" : "rounded-tl-sm",
          )}
          style={
            isUser
              ? { background: "linear-gradient(135deg, #0f172a 0%, #334155 100%)", color: "#fff" }
              : { background: "#fff", border: "1px solid #e2e8f0", color: "#0f172a", boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }
          }
        >
          {showTyping ? <TypingDots /> : msg.text}
        </div>
      </div>
    </div>
  )
}

function TypingDots() {
  return (
    <span style={{ display: "inline-flex", gap: 4, padding: "2px 0" }}>
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          style={{
            width: 6, height: 6, borderRadius: "50%",
            background: "#94a3b8",
            animation: `typingPulse 1.3s ${i * 0.18}s infinite ease-in-out`,
          }}
        />
      ))}
      <style>{`
        @keyframes typingPulse {
          0%, 60%, 100% { opacity: 0.3; transform: translateY(0); }
          30% { opacity: 1; transform: translateY(-2px); }
        }
      `}</style>
    </span>
  )
}
